import React, { useState, useEffect, useCallback, useRef } from 'react';
import { estimateShippingBox, estimateShippingBoxFromVariations, applyPromoCode, removePromoCode } from '../services/api';
import '../styles/cart.css';
import '../styles/global.css';
import { useNavigate } from 'react-router-dom';
import useNotification from '../hooks/useNotification';
import useStickyTop, { stickyTopStyle } from '../hooks/useStickyTop';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { buildAssetUrl } from '../utils/imageResolver';
import { Minus, Plus, Trash2 } from 'lucide-react';
import LoginRequiredDialog from '../components/Dialogs/LoginRequiredDialog';
import { trackPromoEvent, trackEvent } from '../utils/telemetry';
import { memberDisplayLabel } from '../components/MyStore/CreateLotModal';
import { isSexedPair } from '../constants/enums';
import { PLANTS_SHIP_WITH_ANIMALS_MESSAGE, isPlantOnlyCart } from '../constants/shippingRules';
import { OOAK_BADGE_TEXT } from '../components/Products/ooakBadge';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import { formatScientificName, formatSpeciesName, formatLocality } from '../utils/scientificName';
import { formatProductName } from '../utils/productName';
import { groupLotLabel } from '../utils/kindLabel';

// R10 (2026-10-07): the neutral "no photo" tile (public/media-placeholders).
export const CART_NO_PHOTO_SRC = NO_PHOTO_SRC;

// Run 28 D / Cowork C2: the line's own ceiling. ONE definition, read by
// the clamp, the input's `max`, and the + button's disabled state — a UI
// that let you press + past a cap the handler then undid is the silent
// failure Cowork filed.
//
// Plain variation lines only: pinned-animal lines are qty-1 by contract,
// and a lot line's availability is the lot's own derivation (CartItem
// .clean deliberately skips the variation count for lots). A PACKAGE is
// a plain variation line whose `count` is its derived box availability,
// so boxes are capped by the same rule.
export const lineStockCap = (item) => {
  const rawCount = parseInt(item?.product_variation?.count, 10);
  if (item?.animal_id || item?.animal || item?.lot_id || item?.lot) return null;
  return Number.isFinite(rawCount) && rawCount > 0 ? rawCount : null;
};

// facility.enums.AnimalTypes.PLANT. A cart of nothing but plant lines
// cannot be shipped (operator rule) and order creation refuses it with
// 422 — the customer used to discover that two steps later, at a
// "Proceed to Payment" click that appeared to do nothing.
// Batch 34 C5: the rule lives in constants/shippingRules.js now (ONE
// implementation, also read by the add-to-cart confirmation); re-exported
// here for the readers that import it from the cart page.
export { isPlantOnlyCart };

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems = [],  // Default to empty array to avoid undefined errors
    isLoading,
    updateItem,
    removeItem,
    clearCart,
    cartWasAdjusted,
    setCartWasAdjusted,
    cart,
    setCart,
    cartId,
    refreshCart,
  } = useCart();


  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();
  // The order summary sticks just under the navbar's MEASURED bottom
  // (cart.css reads it as --ff-sticky-top; R5 B1 review, 2026-10-26).
  const summaryStickyTop = useStickyTop(true);

  const [processingItemId, setProcessingItemId] = useState(null);
  const [errorMessages, setErrorMessages] = useState({});
  const [zipCode, setZipCode] = useState('');
  const [quoteAmount, setQuoteAmount] = useState(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  // Punch #69 — optimistic quantity drafts (key → target qty) + debounce
  // timers. Refs mirror state so timeouts read the LATEST draft.
  const [qtyDrafts, setQtyDrafts] = useState({});
  // Cowork C2/C3 — per-line "capped" FLAG (not the message). A toast
  // alone was missable, which made the clamp read as the box silently
  // refusing the number; this pins a note to the line until the
  // shopper's next change.
  //
  // A flag, not a string: the cap can move under the line (a package
  // resync changes box availability), and a frozen "Only 4 available."
  // beside a max of 3 is a new lie. The text is rendered from the CURRENT
  // cap, the same call the input's max and the + button read.
  const [qtyCapped, setQtyCapped] = useState({});
  const qtyDraftsRef = useRef({});
  const qtyTimersRef = useRef({});
  // (item, updateId) per pending key, so remove/clear can CANCEL a
  // pending sync and checkout/unmount can FLUSH it (adv-review SF3/SF4).
  const qtyPendingRef = useRef({});

  // adv-review SF4: on unmount, FLUSH (not just clear) pending syncs —
  // clearing alone would silently drop the customer's last click. The
  // fired requests land against the app-level CartProvider; this page's
  // own setState calls become no-ops, which React 18 tolerates.
  useEffect(() => () => {
    Object.entries(qtyPendingRef.current).forEach(([key, pending]) => {
      if (pending && qtyDraftsRef.current[key] != null) {
        clearTimeout(qtyTimersRef.current[key]);
        syncQuantity(pending.item, key, pending.updateId);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // CRITICAL FIX: Track successfully applied manual promo in state + localStorage
  // This persists through navigation (checkout) and login redirects
  const [appliedPromoCode, setAppliedPromoCode] = useState(() => {
    try {
      return localStorage.getItem('appliedPromoCode') || '';
    } catch (e) {
      return '';
    }
  });
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [cartChangedSinceQuote, setCartChangedSinceQuote] = useState(false);

  // Calculate subtotal from items (before discounts)
  const subtotal = (cartItems || []).reduce((sum, item) => {
    // I-02b: prefer the server-computed subtotal (animal price overrides
    // are applied server-side); fall back to local math for anon carts.
    const itemSubtotal = item.subtotal != null
      ? parseFloat(item.subtotal)
      : parseFloat(item.product_variation?.latest_price?.price || 0) * item.quantity;
    return sum + itemSubtotal;
  }, 0);

  // Get discount values from cart API response (Phase C)
  const autoDiscount = parseFloat(cart?.auto_discount || 0);
  const promoCodeDiscount = parseFloat(cart?.promo_code_discount || 0);
  const totalDiscount = parseFloat(cart?.total_discount || 0);
  // adv-review N9 (2026-08-07): the two surfaces hand back two shapes.
  // The guest preview returns a LIST of {code, name, discount}; the
  // authenticated CartSerializer returns the normalized breakdown DICT
  // {manual_codes, auto, lines, total_discount, dropped}. `.length` on
  // that dict is undefined, so signed-in customers never saw which promo
  // was applied — the discount amount rendered, the reason for it did not.
  const rawAppliedPromotions = cart?.applied_promotions;
  const appliedPromotions = Array.isArray(rawAppliedPromotions)
    ? rawAppliedPromotions
    : (rawAppliedPromotions?.lines || []);

  // Cowork QA 2026-08-10 (🔴): an OUTBID code — valid, saved, simply worth
  // less than the automatic offer that's applying — used to vanish from
  // the UI entirely (the chip renders on promoCodeDiscount > 0) while
  // localStorage still held it. The customer watched their code disappear
  // without a word. The backend says exactly what happened: the
  // authenticated breakdown carries dropped[reason=not_applied_better_offer],
  // the guest shape carries promo_code_error.NOT_APPLIED_BETTER_OFFER.
  const outbidPromoCode = (
    (!Array.isArray(rawAppliedPromotions)
      && rawAppliedPromotions?.dropped?.find(
        (d) => d.reason === 'not_applied_better_offer')?.code)
    || (cart?.promo_code_error?.code === 'NOT_APPLIED_BETTER_OFFER'
      ? appliedPromoCode : null)
    || null
  );
  const winningOfferName = appliedPromotions[0]?.name || null;

  // Cowork parity run 2026-08-11 (🔴 "$68 promised, $85 charged"): an
  // outbid AUTOMATIC promotion vanished without a word — the product
  // page advertised its price, the cart charged full price, and nothing
  // explained that the shopper is actually receiving a BIGGER discount
  // from a competing offer. The backend now names each outbid auto with
  // its standalone worth vs the winning total; render it as an
  // explanation line under the chips. Authed carts carry it inside the
  // breakdown dict, guest carts at the top level of the preview cart.
  const outbidAutos = (
    (!Array.isArray(rawAppliedPromotions) && rawAppliedPromotions?.outbid_autos)
    || cart?.outbid_autos
    || []
  );

  // #110c (2026-08-17): Buy-4 nudge — the backend names quantity offers
  // within 1-2 items of unlocking (near_miss, engine-discountable lines
  // only; same envelope shapes as outbid_autos: authed carts carry it
  // inside the breakdown dict, guest previews at the cart top level).
  // Render only the NEAREST one — a stack of nudges stops being a nudge.
  const nearMissOffers = (
    (!Array.isArray(rawAppliedPromotions) && rawAppliedPromotions?.near_miss)
    || cart?.near_miss
    || []
  );
  const nearMiss = nearMissOffers[0] || null;
  const nearMissCopy = (nm) => {
    const more = nm.add_qty === 1 ? '1 more' : `${nm.add_qty} more`;
    if (typeof nm.save_amount === 'number') {
      return `Add ${more} to save $${(nm.save_amount / 100).toFixed(2)}`;
    }
    if (typeof nm.save_percent === 'number') {
      return `Add ${more} to get ${Math.round(nm.save_percent)}% off`;
    }
    return `Add ${more} to unlock ${nm.label || nm.name}`;
  };

  // Calculate total after discounts
  const total = subtotal - totalDiscount;
  // S3-01.5 (Cowork 2026-09-05): teach the plants-ship-with-animals rule
  // HERE, where the customer can still act on it, instead of letting
  // checkout refuse it silently two steps later.
  const plantOnly = isPlantOnlyCart(cartItems);

  // Debug: Log when discount values change
  useEffect(() => {
    if (cart && (autoDiscount > 0 || promoCodeDiscount > 0)) {
      console.log('[CART_PAGE] 💰 Discount values updated:', {
        subtotal,
        autoDiscount,
        promoCodeDiscount,
        totalDiscount,
        final_total: total,
        applied_promotions: appliedPromotions.length
      });
    }
  }, [cart?.auto_discount, cart?.promo_code_discount, cart?.total_discount]);

  useEffect(() => {
    if (cartWasAdjusted) {
      const timeout = setTimeout(() => setCartWasAdjusted(false), 7000);
      return () => clearTimeout(timeout);
    }
  }, [cartWasAdjusted, setCartWasAdjusted]);


  // Reset shipping quote when cart items change
  useEffect(() => {
    if (quoteAmount !== null) {
      // Only set flag if we had a quote before
      setCartChangedSinceQuote(true);
    }
    setQuoteAmount(null);
  }, [cartItems]);

  // Refetch cart on page mount and when page becomes visible (authenticated users only)
  useEffect(() => {
    // Only refetch for authenticated users - anonymous users use localStorage
    if (isAuthenticated && refreshCart) {
      // Refetch on mount
      refreshCart();
    }

    // Refetch when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && refreshCart) {
        refreshCart();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated, not refreshCart (prevents infinite loop)

  // Auto-fetch promotions for anonymous users on cart load and changes
  const debounceTimerRef = useRef(null);
  const previousItemsRef = useRef(null);

  const fetchAutoPromotions = useCallback(async () => {
    if (isAuthenticated || !cartItems || cartItems.length === 0) {
      return;
    }

    // Build items payload
    // #57: lot_id must ride along — a pool bundle lives on a STOCK
    // variation, so without it the preview engine cannot tell a bundle
    // line from a plain stock line and counts it toward thresholds.
    const itemsForPreview = cartItems.map(item => ({
      variation_id: item.product_variation.id,
      quantity: item.quantity,
      lot_id: item.lot?.id || item.lot_id || null,
      animal_id: item.animal?.id || item.animal_id || null,
    }));

    // Serialize to detect actual changes (prevent duplicate calls)
    // IMPORTANT: Include appliedPromoCode in the key to detect promo changes
    const itemsKey = JSON.stringify({ items: itemsForPreview, promo: appliedPromoCode });
    if (previousItemsRef.current === itemsKey) {
      return; // No change, skip API call
    }
    previousItemsRef.current = itemsKey;

    try {
      console.log('[AUTO_PROMO] Fetching promotions for anonymous cart:', {
        items: itemsForPreview,
        promo_code: appliedPromoCode || '(none)',
      });

      // CRITICAL FIX: Include manually applied promo code in auto-fetch
      // This preserves manual promo codes through cart changes
      const response = await applyPromoCode(appliedPromoCode || '', itemsForPreview);

      if (response.success && response.cart && setCart) {
        console.log('[AUTO_PROMO] ✅ Promotions fetched:', {
          auto_discount: response.cart.auto_discount,
          promo_code_discount: response.cart.promo_code_discount,
          total_discount: response.cart.total_discount,
          applied_promotions: response.cart.applied_promotions,
        });
        setCart(response.cart);
      }
    } catch (error) {
      // Silent fail for auto-promos (don't show toast)
      console.error('[AUTO_PROMO] Failed to fetch promotions:', error);
    }
  }, [isAuthenticated, cartItems, appliedPromoCode, setCart]);

  useEffect(() => {
    // Debounce to prevent spam during rapid quantity changes
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchAutoPromotions();
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchAutoPromotions]);


  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-loading">
          Loading cart...
        </div>
      </div>
    );
  }
  
  if (!cartItems || !cartItems.length) {
    return (
      <div className="cart-page">
        <div className="cart-empty">
          <h2 className="cart-empty-title">
            Your cart is empty
          </h2>
          <p className="cart-empty-text">
            Add some amazing amphibians to your collection!
          </p>
          <button
            type="button"
            onClick={() => navigate('/frogs')}
            className="ff-btn ff-btn--primary"
          >
            Shop Frogs
          </button>
        </div>
      </div>
    );
  }

  const showMorphNamesByProduct = cartItems.reduce((acc, item) => {
    const productId = item.product_variation.product_id;
    const morphName = item.product_variation.morph?.name?.trim()?.toLowerCase();
    const hasMorph = morphName && morphName !== 'normal';
    acc[productId] = acc[productId] || hasMorph;
    return acc;
  }, {});

  const handleCheckout = () => {
    // Belt and braces: the button is disabled in this state, but a
    // programmatic call must not walk into checkout's 422 either.
    if (isPlantOnlyCart(cartItems)) {
      showNotification(PLANTS_SHIP_WITH_ANIMALS_MESSAGE, 'warning');
      return;
    }
    flushQtySyncs();   // adv-review SF4: the last click must not be dropped behind the order snapshot
    // Track checkout attempt BEFORE any auth gating or navigation
    trackEvent('checkout_attempted', {
      is_authenticated: isAuthenticated,
      cart_id: cartId || null,
      item_count: cartItems?.length || 0,
      subtotal: subtotal,
      has_promo: promoCodeDiscount > 0,
    });

    if (!isAuthenticated) {
      setShowLoginDialog(true);
    } else {
      navigate('/checkout/address');
    }
  };

  const handleLoginRedirect = () => {
    setShowLoginDialog(false);
    navigate('/login');
  };

  const handleSignUpRedirect = () => {
    setShowLoginDialog(false);
    navigate('/signup');
  };


// Punch #69 (Cowork run 5): every stepper click awaited its own server
// round-trip with the buttons DISABLED while in flight, so rapid clicks
// were silently discarded — 1→4 took seven clicks and then overshot.
// The rework: the DISPLAYED quantity updates instantly from a local
// draft, and one debounced request chases the final value. If the user
// keeps clicking while a sync is in flight, the sync re-chases when it
// lands. The server stays the source of truth: on error the draft
// reverts to the server's quantity.
const handleQuantityChange = (item, newQuantity) => {
  const key = item.id ?? item.product_variation.id;
  const updateId = isAuthenticated ? item.id : item.product_variation.id;
  const stockCap = lineStockCap(item);
  const floored = Math.max(1, newQuantity);
  if (stockCap && floored > stockCap) {
    // Cowork C2/C3: the clamp was silent in practice — a toast the
    // shopper can miss, and the number just snapping back. Keep the
    // toast, but ALSO pin an inline note to the line itself until the
    // next change, so the cap explains itself where it happened.
    showNotification(`Only ${stockCap} available.`, 'warning');
    setQtyCapped(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  } else {
    setQtyCapped(prev => (prev[key] ? { ...prev, [key]: false } : prev));
  }
  const next = stockCap ? Math.min(stockCap, floored) : floored;
  qtyDraftsRef.current[key] = next;
  setQtyDrafts({ ...qtyDraftsRef.current });
  if (quoteAmount !== null) {
    setCartChangedSinceQuote(true);
  }
  setQuoteAmount(null);
  qtyPendingRef.current[key] = { item, updateId };
  clearTimeout(qtyTimersRef.current[key]);
  qtyTimersRef.current[key] = setTimeout(() => syncQuantity(item, key, updateId), 450);
};

// adv-review SF3: a Remove (or Clear Cart) during the 450ms debounce
// window left the sync to fire against a deleted row — 404, and a
// "Failed to update quantity" toast right after a successful removal.
const cancelQtySync = (key) => {
  clearTimeout(qtyTimersRef.current[key]);
  delete qtyTimersRef.current[key];
  delete qtyDraftsRef.current[key];
  delete qtyPendingRef.current[key];
  setQtyDrafts({ ...qtyDraftsRef.current });
};

// adv-review SF4: fire every pending sync NOW — before checkout
// navigation or unmount — so the customer's last click is never silently
// dropped behind the order snapshot.
const flushQtySyncs = () => {
  Object.entries(qtyPendingRef.current).forEach(([key, pending]) => {
    if (pending && qtyDraftsRef.current[key] != null) {
      clearTimeout(qtyTimersRef.current[key]);
      syncQuantity(pending.item, key, pending.updateId);
    }
  });
};

const syncQuantity = async (item, key, updateId) => {
  const target = qtyDraftsRef.current[key];
  if (target == null) return;
  setProcessingItemId(key);
  try {
    await updateItem(updateId, target);
    // adv-review BLOCKER (2026-08-10): the authed updateItem only
    // SCHEDULES a debounced refetch — deleting the draft here made the
    // display snap back to the stale quantity for ~500ms on every single
    // click, the exact jank this rework exists to kill. Refresh FIRST so
    // item.quantity is already the new value when the draft drops.
    if (isAuthenticated && refreshCart) {
      await refreshCart();
    }
    setErrorMessages((prev) => ({ ...prev, [key]: null }));
    if (qtyDraftsRef.current[key] !== target) {
      // The user kept clicking while we were in flight — chase the newer
      // value rather than declaring the stale one final. Clear the slot
      // first: a click mid-flight stored a 450ms timer here, and
      // overwriting without clearing left BOTH to fire (adv-review SF2 —
      // two concurrent PUTs with no ordering guarantee).
      clearTimeout(qtyTimersRef.current[key]);
      qtyTimersRef.current[key] = setTimeout(() => syncQuantity(item, key, updateId), 150);
    } else {
      delete qtyDraftsRef.current[key];
      delete qtyPendingRef.current[key];
      setQtyDrafts({ ...qtyDraftsRef.current });
    }
  } catch (error) {
    const errorCode = error?.response?.data?.error_code;
    // Cowork run 7 (🔴): DRF returns LIST-shaped errors, and storing the
    // raw value put `["You can't add more than 20 of this item."]` —
    // brackets and quotes — in front of a customer. Unwrap once, here,
    // before anything renders it.
    const rawPayload = error?.response?.data?.message || error?.response?.data?.detail;
    const rawMessage = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
    let message = "Failed to update quantity.";
    if (errorCode === "OVER_QUANTITY_LIMIT") {
      message = rawMessage || "Requested quantity exceeds available inventory.";
    }
    setErrorMessages((prev) => ({ ...prev, [key]: message }));
    showNotification(Array.isArray(message) ? message[0] : message, 'error');
    // Revert the display to the server's truth.
    delete qtyDraftsRef.current[key];
    setQtyDrafts({ ...qtyDraftsRef.current });
  } finally {
    setProcessingItemId(null);
  }
};

const handleRemove = async (item) => {
  // Cowork QA F2 (punch #54): pass the SERVER row id whenever the line
  // has one (lot bundles / pinned animals always do — they live in the
  // backend cart even for anonymous sessions). Passing the variation id
  // for those lines made removeItem fall back to a local-only filter
  // that never told the backend, so the line resurrected on refetch.
  const id = item.id ?? item.product_variation.id;
  cancelQtySync(id);   // adv-review SF3: a pending sync must die with the row
  // ...and so must its cap note. Guest lines are keyed by VARIATION id,
  // so removing a capped line and re-adding it would otherwise inherit
  // the old line's note.
  setQtyCapped(prev => (prev[id] ? { ...prev, [id]: false } : prev));
  setProcessingItemId(id);
  if (quoteAmount !== null) {
    setCartChangedSinceQuote(true);
  }
  setQuoteAmount(null);
  try {
    await removeItem(id);
  } catch (e) {
    // console.warn survives the production terser strip
    console.warn('[CART] remove failed:', e?.response?.status || e?.message);
    showNotification("Failed to remove item.", "error");
  } finally {
    setProcessingItemId(null);
  }
}

  const handleClearCart = async () => {
    setProcessingItemId("CLEAR_CART");
    Object.keys(qtyTimersRef.current).forEach(cancelQtySync);   // SF3
    try {
      await clearCart();
      setQtyCapped({});   // nothing left to be capped
      // Cowork QA 2026-08-10: an emptied cart has nothing for a code to
      // apply to, but the key survived and carried the code into the
      // customer's NEXT session — a stale discount appearing from
      // nowhere is a support ticket. Clear it with the cart.
      setAppliedPromoCode('');
      try {
        localStorage.removeItem('appliedPromoCode');
      } catch (storageError) {
        console.warn('[PROMO] Failed clearing appliedPromoCode:', storageError);
      }
    } catch (e) {
      // F2: clearCart no longer swallows failures — surface them here.
      console.warn('[CART] clear failed:', e?.response?.status || e?.message);
      showNotification("Failed to clear cart.", "error");
    } finally {
      setProcessingItemId(null);
    }
  };

  // Phase C: Apply promo code handler
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      showNotification("Please enter a promo code.", "warning");
      return;
    }

    const codeToApply = promoCode.trim();
    console.log('[PROMO] Applying promo code:', codeToApply);

    setIsApplyingPromo(true);
    try {
      // For anonymous users, pass items array for preview mode
      const itemsForPreview = !isAuthenticated ? cartItems.map(item => ({
        variation_id: item.product_variation.id,
        quantity: item.quantity,
        price: item.product_variation.price,
        lot_id: item.lot?.id || item.lot_id || null,  // #57, see above
        animal_id: item.animal?.id || item.animal_id || null,
      })) : null;

      const response = await applyPromoCode(codeToApply, itemsForPreview);
      if (response.success) {
        console.log('[PROMO] ✅ Success:', {
          message: response.message,
          cart_data: response.cart,
          auto_discount: response.cart?.auto_discount,
          promo_code_discount: response.cart?.promo_code_discount,
          total_discount: response.cart?.total_discount
        });

        // Success toast
        showNotification(response.message || "Promo code applied!", "success");

        // CRITICAL FIX: Save the applied promo code to persist through cart changes + checkout
        setAppliedPromoCode(codeToApply);
        try {
          localStorage.setItem('appliedPromoCode', codeToApply);
        } catch (error) {
          console.error('[PROMO] Failed to save promo code to localStorage:', error);
        }

        // Clear input on success
        setPromoCode('');

        // CRITICAL: Use cart data from response immediately to avoid stale data
        if (response.cart && setCart) {
          console.log('[PROMO] 📦 Updating cart context with response data');
          setCart(response.cart);
          console.log('[PROMO] 🎯 Cart updated - discounts should now appear in UI');
        } else {
          // Fallback: Refetch if response doesn't include cart or setCart unavailable
          console.log('[PROMO] ⚠️ No cart in response, refetching...');
          await refreshCart();
        }
      } else {
        // Handle non-error response but not successful
        console.log('[PROMO] Non-error but not successful:', response);
        showNotification("Failed to apply promo code.", "error");
      }
    } catch (error) {
      // #52: ONE genuine error line (survives prod); detail rides along.
      console.error('[PROMO] ❌ Error applying promo code:', error,
        error?.response?.status, error?.response?.data);

      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || "Invalid promo code.";

      // Friendly error messages based on error code
      const friendlyMessages = {
        // Cowork parity run 2026-08-11: guest and authed rejections must
        // read identically — and NOT_FOUND also covers inactive/expired
        // codes, so it must not claim the code "doesn't exist"
        'NOT_FOUND': "This promo code is expired or invalid",
        'EXPIRED': "This promo code has expired.",
        'USAGE_LIMIT_REACHED': "This promo code has reached its usage limit.",
        'NOT_ELIGIBLE': errorMessage, // Use specific message from backend
        'USER_LIMIT_REACHED': "You've already used this promo code."
      };

      const message = friendlyMessages[errorCode] || errorMessage;
      // #52: pre-call trace, not an error — console.log is stripped in prod.
      console.log('[PROMO] showing error toast:', { code: errorCode, message });

      // CRITICAL: Ensure toast appears
      try {
        showNotification(message, "error");
      } catch (toastError) {
        console.error('[PROMO] ❌ Toast failed:', toastError);
        // Fallback: Use alert if toast system fails
        alert(`Promo Code Error: ${message}`);
      }

      // Cowork QA 2026-08-10: leave a rejected code in the box and the
      // panel reads "NOTACODE" directly above "✓ Promo code applied" —
      // as if the junk were the applied code. Clear it.
      setPromoCode('');
    } finally {
      setIsApplyingPromo(false);
      console.log('[PROMO] Apply button re-enabled');
    }
  };

  // Phase C: Remove promo code
  const handleRemovePromoCode = async () => {
    const codeToRemove = appliedPromoCode;
    console.log('[PROMO] Removing promo code:', codeToRemove);

    // Clear local state immediately for responsive UI
    setPromoCode('');
    setAppliedPromoCode('');
    try {
      localStorage.removeItem('appliedPromoCode');
    } catch (error) {
      console.error('[PROMO] Failed to remove promo code from localStorage:', error);
    }

    if (isAuthenticated) {
      // Call backend to remove promo code from cart
      try {
        const response = await removePromoCode(codeToRemove);
        console.log('[PROMO] Remove response:', response);

        // Update cart with response data
        if (response.success && response.cart && setCart) {
          console.log('[PROMO] Updating cart after promo removal');
          setCart(response.cart);
        }
        showNotification(response.message || "Promo code removed.", "info");
      } catch (error) {
        console.error('[PROMO] Failed to remove promo code from backend:', error);
        showNotification("Failed to remove promo code.", "error");
        // Fallback: refresh cart to get current state
        await refreshCart();
      }
    } else {
      // For anonymous users, trigger fetchAutoPromotions via useEffect
      // The useEffect watching appliedPromoCode will re-fetch auto-promos
      // Track promo_removed telemetry for anonymous users
      if (codeToRemove) {
        trackPromoEvent('promo_removed', codeToRemove, { anonymous: true });
      }
      showNotification("Promo code removed.", "info");
    }
  };

  // Shipping quote request handler
  const ensureCartReady = async () => {
    if (isAuthenticated && !cartId) {
      try {
        if (refreshCart) {
          await refreshCart();
        }
        if (!cartId) {
          return false;
        }
      } catch (error) {
        return false;
      }
    }
    return true;
  };

  const handleGetShippingQuote = async () => {
    if (isQuoteLoading) return;

    setIsQuoteLoading(true);
    try {
      if (!zipCode) {
        showNotification("Please enter a ZIP code.", "warning");
        return;
      }

      if (!cartItems || cartItems.length === 0) {
        showNotification("Add items to your cart to estimate shipping.", "info");
        return;
      }

      let response;
      let retryCount = 0;
      const maxRetries = 1;

      const attemptQuote = async () => {
        if (isAuthenticated) {
          const cartReady = await ensureCartReady();
          if (!cartReady || !cartId) {
            throw { code: 'cart_not_ready', message: 'Cart not initialized' };
          }

          const payload = {
            destination_zip: zipCode,
            residential: true,
            zip: zipCode,
          };
          response = await estimateShippingBox(cartId, payload);
        } else {
          const variationItems = cartItems.map((item) => ({
            variation: item?.product_variation?.id,
            quantity: item.quantity,
            // I-02d: lot lines ship every member — the API layer sends
            // lot_ids and the server resolves the member count.
            lot: item?.lot?.id || item?.lot_id || null,
          }));
          response = await estimateShippingBoxFromVariations(variationItems, zipCode);
        }
        return response;
      };

      try {
        response = await attemptQuote();
      } catch (error) {
        if (error?.response?.data?.code === 'cart_not_ready' && retryCount < maxRetries) {
          retryCount++;
          await ensureCartReady();
          response = await attemptQuote();
        } else {
          throw error;
        }
      }
      
      const finalCost = response?.quote?.final_shipping_cost ?? response?.final_shipping_cost;
      
      if (finalCost !== undefined && finalCost !== null) {
        setQuoteAmount(finalCost);
        setCartChangedSinceQuote(false); // Clear flag when new quote obtained
        const serviceName = response?.quote?.service_name || 'FedEx';
        showNotification(`Shipping quote (${serviceName}): $${finalCost.toFixed(2)}`, 'info');
      } else {
        showNotification(`No final shipping cost returned from server.`, 'warning');
      }
    } catch (err) {
      if (err?.code === 'cart_not_ready') {
        showNotification('Cart is initializing. Please try again.', 'warning');
      } else if (err?.response?.status === 400) {
        showNotification('Unable to calculate shipping quote. Please verify your ZIP code.', 'error');
      } else {
        showNotification('Failed to get shipping quote.', 'error');
      }
    } finally {
      setIsQuoteLoading(false);
    }
  };

  const getTitle = (variation) =>
    cartLineTitle(variation, showMorphNamesByProduct[variation?.product_id]);

  const getMetadataLine = (variation) => cartLineMeta(variation);

  const showMorphNames = cartItems.some((item) => {
    const name = item?.product_variation?.morph?.name?.trim();
    return name && name.length > 0 && name.toLowerCase() !== 'normal';
  });


  return (
    <div className="cart-page">
      <div className="cart-layout">
        <div className="cart-main">
          <h2 className="cart-page-title" style={{ textAlign: 'left', marginBottom: 'var(--ff-space-xl)' }}>
            Your Cart ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})
          </h2>
          
          {cartWasAdjusted && (
            <div className="cart-adjusted-warning">
              Some items in your cart were adjusted due to limited stock.
            </div>
          )}

          <div className="cart-items-container">
        {cartItems.map((item) => {
          const variation = item.product_variation || {};
          if (!variation?.id) {
            return null;
          }
          // I-02b: server subtotal is animal-aware (price overrides)
          const subtotal = item.subtotal != null
            ? parseFloat(item.subtotal)
            : parseFloat(variation.latest_price?.price || 0) * item.quantity;
          const price = item.quantity > 0 ? subtotal / item.quantity : subtotal;
          const isProcessing = processingItemId === (item.id ?? item.product_variation.id);
          const baseTitle = getTitle(variation, showMorphNames);
          const animalTag = item.animal
            ? (item.animal.name || `#${String(item.animal.id).slice(0, 6).toUpperCase()}`)
            : null;
          // I-02d — lot lines carry the lot title the same way pinned
          // animal lines carry the animal identity.
          const lotTag = !animalTag && item.lot ? item.lot.title : null;
          const title = animalTag
            ? `${baseTitle} — ${animalTag}`
            : lotTag ? `${baseTitle} — ${lotTag}` : baseTitle;
          const lotMembers = item.lot?.members || [];
          const isPoolLot = item.lot?.lot_type === 'pool_group';
          const lotBadge = item.lot
            ? (item.lot.lot_type === 'pair'
                // Q-sex-certainty: "Sexed" only over two CONFIRMED sexes.
                ? (isSexedPair(lotMembers) ? 'Sexed Pair' : 'Pair')
                // I-02d.2 — pool bundles have no members; N = quantity
                : isPoolLot ? `Group of ${item.lot.quantity}`
                // A group of 1.2 / 2.1 is a trio (the one helper's port).
                : groupLotLabel(lotMembers))
            : null;
          const itemId = item.id ?? item.product_variation.id;
          const itemUnavailable = item.is_available === false;

          return (
            <div key={item.id || `variation-${item.product_variation?.id}-${item.animal?.id || item.lot?.id || 'stock'}`} className={`cart-item-card${itemUnavailable ? ' cart-item-unavailable' : ''}`}>
              {/* Fable ruling R10 (2026-10-07, Cowork run 31): a line with no
                  photo shows the app's NEUTRAL "no photo" tile — never the
                  old 1x1 translucent-blue placeholder pixel, which the image's
                  box stretched into a purple square. A photo that fails to
                  load falls back to the same tile. */}
              <img
                src={
                  buildAssetUrl(variation.cover_display_url || variation.cover_thumb_url || variation.image_url) || 
                  CART_NO_PHOTO_SRC
                }
                alt={title}
                className="cart-item-image"
                loading="lazy"
                data-cart-no-photo={buildAssetUrl(variation.cover_display_url || variation.cover_thumb_url || variation.image_url) ? undefined : '1'}
                onError={(e) => {
                  if (!e.currentTarget.src.endsWith(CART_NO_PHOTO_SRC)) {
                    e.currentTarget.src = CART_NO_PHOTO_SRC;
                    e.currentTarget.setAttribute('data-cart-no-photo', '1');
                  }
                }}
              />
              
              <div className="cart-item-details">
                <h4 className="cart-item-title">
                  {title}
                </h4>

                {/* I-02d — lot lines: type badge + member roster */}
                {item.lot && (
                  <div className="cart-item-meta" style={{ marginBottom: 'var(--ff-space-xs)' }}>
                    <span
                      className="ff-badge"
                      style={{
                        background: 'var(--ff-surface-tertiary)',
                        color: 'var(--ff-text-primary)',
                        border: '1px solid var(--ff-border-medium, #dee2e6)',
                        borderRadius: '999px',
                        padding: '2px 10px',
                        fontSize: 'var(--ff-font-size-xs, 0.75rem)',
                        fontWeight: 700,
                        marginRight: 'var(--ff-space-sm)',
                      }}
                    >
                      {lotBadge}
                    </span>
                    <span className="text-muted" style={{ fontSize: 'var(--ff-font-size-sm, 0.875rem)' }}>
                      {/* Cowork F1 — shared member-identity rule: name or
                          #SHORTID · ~age, never "Unnamed". I-02d.2 pool
                          bundles have no roster — animals are pulled
                          from stock at checkout. */}
                      {isPoolLot
                        ? 'Pulls from available stock'
                        : lotMembers.map((m) => {
                          const glyph = m.gender === 0 ? '♂' : m.gender === 1 ? '♀' : '?';
                          return `${glyph} ${memberDisplayLabel(m)}`;
                        }).join(' · ')}
                    </span>
                  </div>
                )}

                {itemUnavailable && (
                  <div style={{
                    background: 'var(--ff-semantic-danger, #dc3545)',
                    color: '#fff',
                    padding: 'var(--ff-space-xs) var(--ff-space-sm)',
                    borderRadius: 'var(--ff-radius-sm, 4px)',
                    fontSize: 'var(--ff-font-size-sm, 0.875rem)',
                    fontWeight: 600,
                    marginBottom: 'var(--ff-space-xs)',
                  }}>
                    This item is no longer available. Please remove it to continue.
                  </div>
                )}

                {getMetadataLine(variation)}
                
                <div className="cart-item-pricing">
                  <div className="cart-item-price-info">
                    Unit Price: <strong>${price ? price.toFixed(2) : 'N/A'}</strong>
                  </div>
                  
                  <div className="cart-item-price-info">
                    Line Total: <strong>${subtotal.toFixed(2)}</strong>
                  </div>
                </div>

                <div className="cart-item-controls">
                  <label className="cart-quantity-label">
                    Quantity:
                  </label>
                  
                  {item.animal || item.lot ? (
                    /* I-02b — individual animals are singular: no
                       quantity controls, just a fixed indicator.
                       I-02d — lots get the same treatment (one lot =
                       one unit; members ship together). */
                    <div className="cart-quantity-group">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {item.lot ? `${lotBadge} · Qty 1` : `${OOAK_BADGE_TEXT} · Qty 1`}
                      </span>
                    </div>
                  ) : (
                  <div className="cart-quantity-group">
                    {/* Punch #69: buttons stay ENABLED during syncs —
                        the draft absorbs every click and one debounced
                        request chases the final value. */}
                    <button
                      onClick={() => handleQuantityChange(item, (qtyDrafts[item.id ?? item.product_variation.id] ?? item.quantity) - 1)}
                      disabled={(qtyDrafts[item.id ?? item.product_variation.id] ?? item.quantity) <= 1}
                      type="button"
                      className="ff-btn ff-btn--secondary ff-btn--icon ff-btn--sm cart-quantity-btn"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={16} />
                    </button>
                    
                    <input
                      type="number"
                      min="1"
                      max={lineStockCap(item) ?? undefined}
                      value={(qtyDrafts[item.id ?? item.product_variation.id] ?? item.quantity)}
                      onChange={(e) => {
                        // #78: `parseInt(...) || 1` rewrote the box to "1" on
                        // every keystroke, so Backspace could never clear it —
                        // typing 12 meant selecting the 1 first. Allow a
                        // transient empty value locally; the clamp happens on
                        // blur (and handleQuantityChange still floors at 1 for
                        // the debounced server sync).
                        const raw = e.target.value;
                        const key = item.id ?? item.product_variation.id;
                        if (raw === '') {
                          // Cancel the armed debounce too — otherwise the
                          // PREVIOUS keystroke's value commits to the server
                          // while the box sits empty under the cursor.
                          clearTimeout(qtyTimersRef.current[key]);
                          setQtyDrafts(prev => ({ ...prev, [key]: '' }));
                          return;
                        }
                        const parsed = parseInt(raw, 10);
                        if (Number.isNaN(parsed)) return;
                        handleQuantityChange(item, parsed);
                      }}
                      onBlur={(e) => {
                        // Empty (or sub-1) on blur snaps back to the last
                        // real quantity rather than leaving a blank box.
                        const key = item.id ?? item.product_variation.id;
                        const parsed = parseInt(e.target.value, 10);
                        if (e.target.value === '' || Number.isNaN(parsed) || parsed < 1) {
                          handleQuantityChange(item, item.quantity || 1);
                        }
                      }}
                      className="cart-quantity-input"
                      style={isProcessing ? { opacity: 0.6 } : undefined}
                      aria-label={`Quantity for ${title}`}
                    />
                    
                    <button
                      onClick={() => handleQuantityChange(item, (qtyDrafts[item.id ?? item.product_variation.id] ?? item.quantity) + 1)}
                      disabled={
                        !!lineStockCap(item)
                        && (qtyDrafts[item.id ?? item.product_variation.id]
                            ?? item.quantity) >= lineStockCap(item)
                      }
                      type="button"
                      className="ff-btn ff-btn--secondary ff-btn--icon ff-btn--sm cart-quantity-btn"
                      aria-label={
                        lineStockCap(item)
                        && (qtyDrafts[item.id ?? item.product_variation.id]
                            ?? item.quantity) >= lineStockCap(item)
                          ? `Increase quantity — only ${lineStockCap(item)} available`
                          : 'Increase quantity'
                      }
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  )}
                  {/* Always mounted: a role="status" region that is
                      conditionally rendered is announced by nothing —
                      screen readers watch a region that already exists
                      for changes. Empty until the cap bites. */}
                  <div
                    className={`cart-quantity-note${
                      qtyCapped[item.id ?? item.product_variation?.id]
                        && lineStockCap(item) ? '' : ' cart-quantity-note--empty'}`}
                    role="status"
                    aria-live="polite"
                    data-testid={`qty-note-${item.id ?? item.product_variation?.id}`}
                  >
                    {qtyCapped[item.id ?? item.product_variation?.id]
                      && lineStockCap(item)
                      ? `Only ${lineStockCap(item)} available.`
                      : ''}
                  </div>
                  
                  <button
                    onClick={() => handleRemove(item)}
                    disabled={isProcessing}
                    type="button"
                    className="ff-btn ff-btn--danger ff-btn--sm cart-remove-btn"
                    aria-label={`Remove ${title} from cart`}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>

                {errorMessages[itemId] && (
                  <div className="cart-item-error">
                    {errorMessages[itemId]}
                  </div>
                )}
              </div>
            </div>
          );
        })}
          </div>
        </div>
        
        {/* Sticky Order Summary */}
        <div className="cart-summary" style={stickyTopStyle(summaryStickyTop)}>
          <h3 className="cart-summary-title">
            Order Summary
          </h3>
          
          <div className="cart-shipping-section">
            <h4 className="cart-shipping-title">
              Estimate Shipping
            </h4>
            
            <div>
              <label className="cart-shipping-label">
                ZIP Code:
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="e.g., 66215"
                maxLength={10}
                className="cart-shipping-input"
              />
              
              <button
                type="button"
                onClick={handleGetShippingQuote}
                disabled={!zipCode || isQuoteLoading || !cartItems || cartItems.length === 0}
                className="ff-btn ff-btn--secondary"
              >
                {isQuoteLoading ? "Getting Quote..." : "Get Quote"}
              </button>
              
              {isQuoteLoading && (
                <div className="cart-quote-loading">
                  Loading...
                </div>
              )}
              
              {(!cartItems || cartItems.length === 0) && (
                <div className="cart-quote-loading">
                  Add items to your cart to estimate shipping.
                </div>
              )}

              {cartChangedSinceQuote && zipCode && (
                <div style={{
                  marginTop: 'var(--ff-space-sm)',
                  padding: 'var(--ff-space-sm)',
                  backgroundColor: 'var(--ff-semantic-warning-bg, #fef3c7)',
                  border: '1px solid var(--ff-semantic-warning, #fcd34d)',
                  borderRadius: 'var(--ff-radius-sm)',
                  fontSize: '0.875rem',
                  color: 'var(--ff-text-primary)'
                }}>
                  ⚠️ Cart changed — re-quote shipping
                </div>
              )}

              {quoteAmount !== null && !cartChangedSinceQuote && (
                <div className="cart-quote-result">
                  Estimated Shipping: <strong>${quoteAmount.toFixed(2)}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Phase C: Promo Code Input */}
          <div className="cart-promo-section" style={{ marginBottom: 'var(--ff-space-lg)', padding: 'var(--ff-space-lg)', backgroundColor: 'var(--ff-surface-secondary)', borderRadius: 'var(--ff-radius-md)' }}>
            <h4 style={{ marginBottom: 'var(--ff-space-md)', fontSize: '0.95rem', fontWeight: '600' }}>
              Promo Code
            </h4>
            {/* Cowork mobile sweep 2026-08-11: the inline flex styles let
                the input collapse to 18px on phones; the row's flex
                contract now lives in cart.css (.cart-promo-row). */}
            <div className="cart-promo-row">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                disabled={isApplyingPromo}
                className="cart-shipping-input"
              />
              <button
                type="button"
                onClick={handleApplyPromoCode}
                disabled={!promoCode.trim() || isApplyingPromo}
                className="ff-btn ff-btn--secondary"
              >
                {isApplyingPromo ? "Applying..." : "Apply"}
              </button>
            </div>
            {promoCodeDiscount > 0 && (
              <div style={{ marginTop: 'var(--ff-space-sm)', fontSize: '0.875rem', color: 'var(--ff-semantic-success)' }}>
                ✓ Promo code applied
                <button
                  type="button"
                  onClick={handleRemovePromoCode}
                  className="ff-btn ff-btn--link p-0 cart-promo-remove"
                >
                  Remove
                </button>
              </div>
            )}
            {/* Cowork QA 2026-08-10: the outbid case gets its own chip —
                the code hasn't failed and hasn't gone anywhere, it's just
                being out-earned right now. If the cart changes it starts
                paying again, which without this line looked like a
                discount appearing from nowhere. */}
            {promoCodeDiscount <= 0 && outbidPromoCode && (
              <div style={{ marginTop: 'var(--ff-space-sm)', fontSize: '0.875rem', color: 'var(--ff-text-secondary)' }}>
                ✓ {outbidPromoCode} is saved on your order
                {winningOfferName
                  ? ` — ${winningOfferName} saves you more right now.`
                  : ' — a better offer applies right now.'}
                <button
                  type="button"
                  onClick={handleRemovePromoCode}
                  className="ff-btn ff-btn--link p-0 cart-promo-remove"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="cart-totals">
            <div className="cart-total-row subtotal">
              <span className="cart-total-label">Subtotal:</span>
              <strong className="cart-total-value">${subtotal.toFixed(2)}</strong>
            </div>

            {/* #110c — Buy-4 nudge: a threshold offer within reach gets
                one helpful line at the moment it could close. Quiet
                surface, orange accent (attention, not a deal banner). */}
            {nearMiss && (
              <div style={{
                margin: 'var(--ff-space-sm) 0',
                padding: 'var(--ff-space-sm) var(--ff-space-md)',
                background: 'var(--ff-surface-secondary)',
                borderLeft: '3px solid var(--ff-brandOrange, #f2673a)',
                borderRadius: 'var(--ff-radius-sm, 4px)',
                fontSize: '0.875rem',
                color: 'var(--ff-text-primary)',
              }}>
                {nearMissCopy(nearMiss)}
                <span style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>
                  {' '}— {nearMiss.name}
                </span>
              </div>
            )}

            {/* Phase C: Discount Display — #90b shared .discount-breakdown
                classes (cart.css), extracted from the inline styles this
                page triplicated with checkout + order detail. */}
            {totalDiscount > 0 && (
              <div className="discount-breakdown">
                <div className="discount-breakdown__title">
                  Discounts Applied
                </div>
                {autoDiscount > 0 && (
                  <div className="discount-breakdown__row">
                    <span>Automatic discount:</span>
                    <span className="discount-breakdown__amount">-${autoDiscount.toFixed(2)}</span>
                  </div>
                )}
                {promoCodeDiscount > 0 && (
                  <div className="discount-breakdown__row">
                    <span>Promo code:</span>
                    <span className="discount-breakdown__amount">-${promoCodeDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="discount-breakdown__row discount-breakdown__row--total">
                  <span>Total discount:</span>
                  <span className="discount-breakdown__amount">-${totalDiscount.toFixed(2)}</span>
                </div>
                {appliedPromotions.length > 0 && (
                  <div className="discount-breakdown__promos">
                    {appliedPromotions.map((promo, idx) => (
                      <div key={idx} className="discount-breakdown__promo-line">
                        • {promo.name}
                        {typeof promo.amount === 'number' && (
                          <span className="discount-breakdown__amount">
                            {' '}−${(promo.amount / 100).toFixed(2)}
                          </span>
                        )}
                      </div>
                    ))}
                    {/* I-02e 58c — since deviation D4 the breakdown renders
                        WINNERS ONLY, and a winner is a single candidate: one
                        exclusive offer, or one stack-group's offers pooled
                        (autos and codes alike — codes no longer free-ride).
                        So any two rendered lines genuinely combined, and the
                        earlier source==='auto' filter (adv-review SF8, which
                        predates D4) would wrongly hide the note for a mixed
                        auto+code pool. */}
                    {appliedPromotions.length > 1 && (
                      <div className="discount-breakdown__note">
                        These offers combine.
                      </div>
                    )}
                    {outbidAutos.map((ob, idx) => (
                      <div key={`outbid-auto-${idx}`} className="discount-breakdown__note">
                        {ob.name} can't combine with these offers — you're getting the better deal
                        {/* adv-review SF3: a TIE goes to the exclusive and the
                            loser stays outbid — "(−$20.00 instead of −$20.00)"
                            reads as a glitch, so amounts render only when the
                            winner genuinely pays more. */}
                        {typeof ob.would_pay === 'number' && typeof ob.better_pays === 'number' && ob.better_pays > ob.would_pay && (
                          <>
                            {' '}(−${(ob.better_pays / 100).toFixed(2)} instead of −${(ob.would_pay / 100).toFixed(2)})
                          </>
                        )}
                        .
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {quoteAmount !== null && (
              <>
                <div className="cart-total-row shipping">
                  <span>Estimated Shipping:</span>
                  <span>${quoteAmount.toFixed(2)}</span>
                </div>

                <div className="cart-total-row grand-total">
                  <span className="cart-total-label">Estimated Total:</span>
                  <span className="cart-total-value">${(total + quoteAmount).toFixed(2)}</span>
                </div>
              </>
            )}

            <div className="cart-actions">
              {plantOnly && (
                <div
                  className="alert alert-warning"
                  role="alert"
                  data-testid="cart-plants-advisory"
                >
                  {PLANTS_SHIP_WITH_ANIMALS_MESSAGE}
                </div>
              )}
              <button
                type="button"
                onClick={handleCheckout}
                className="ff-btn ff-btn--primary ff-btn--block cart-checkout-btn"
                disabled={cart?.has_unavailable_items || plantOnly}
                title={
                  cart?.has_unavailable_items
                    ? 'Remove unavailable items to continue'
                    : plantOnly
                      ? PLANTS_SHIP_WITH_ANIMALS_MESSAGE
                      : undefined
                }
              >
                {cart?.has_unavailable_items
                  ? 'Remove Unavailable Items to Checkout'
                  : plantOnly
                    ? 'Add an Animal to Check Out'
                    : 'Proceed to Checkout'}
              </button>
              
              <button
                type="button"
                onClick={handleClearCart}
                disabled={processingItemId === "CLEAR_CART"}
                className="ff-btn ff-btn--danger ff-btn--sm cart-clear-btn"
              >
                {processingItemId === "CLEAR_CART" ? "Clearing..." : "Clear Cart"}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <LoginRequiredDialog
        show={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onConfirm={handleLoginRedirect}
        onSignUp={handleSignUpRedirect}
      />
    </div>
  );
};


export default CartPage;


/**
 * The cart line's TITLE, extracted so it can be pinned without mounting
 * the page (the isPlantOnlyCart precedent). `showMorphNames` is the
 * page's per-product toggle, passed in rather than read from state.
 */
export const cartLineTitle = (variation, showMorphNames) => {
  const getSpeciesName = () => {
    if (variation?.common_names?.[0]) return variation.common_names[0];
    // #121 (2026-08-17): the API sends FLAT `genus` / `species` strings,
    // not a nested `species` object — the old `variation.species.genus`
    // never resolved, so locality-named morphs with no common name (e.g.
    // Azureus) fell through to the literal "Product". Use the flat fields.
    if (variation?.genus && variation?.species) {
      return formatSpeciesName(variation, { withLocality: false });
    }
    if (variation?.species) return variation.species;
    if (variation?.name) return variation.name;
    return 'Product';
  };

  // S3-01.5 (Cowork 2026-09-05): a PACKAGE is sold under its own name.
  // Composing from genus+species named the anchor COMPONENT instead —
  // the cart line read "QADELETE testplant – Package" for a box called
  // "QADELETE vining sampler".
  if (variation?.is_package && variation?.product_name) {
    return formatProductName(variation, { name: variation.product_name });
  }

  const commonName = getSpeciesName();
  const variationType = variation?.variation_type?.name || 'Variation';
  const morphName = variation?.morph?.name?.trim() || '';

  let morphPart = '';
  if (showMorphNames) {
    morphPart = morphName && morphName.toLowerCase() !== 'normal'
      ? ` (${morphName})`
      : ' (Normal)';
  }

  return `${commonName} – ${variationType}${morphPart}`;
};

/**
 * The small grey line under a cart title.
 *
 * S3-01.6 (Cowork re-check #2) — a PACKAGE line printed an italic
 * SPECIES subtitle here ("QADELETE testplant"), because the only species
 * vocabulary a variation carries is the ANCHOR's. A box says what it is
 * instead: "6 assorted cuttings — our selection", or its component list.
 */
export const cartLineMeta = (variation) => {
  if (variation?.is_package) {
    const summary = (variation.package_summary || '').trim();
    return summary ? (
      <div className="cart-item-meta text-muted small" data-testid="cart-package-summary">
        {summary}
      </div>
    ) : null;
  }

  const genus = variation?.genus || '';
  const species = variation?.species || '';
  const locality = variation?.locality;

  const speciesType = variation?.species_type;
  const sciName = genus && species
    ? <em>{formatScientificName(genus, species, undefined, speciesType)}</em> : null;
  const local = locality ? formatLocality(locality, speciesType) : null;

  const parts = [];
  if (sciName) parts.push(sciName);
  if (local) parts.push(local);

  return parts.length > 0 ? (
    <div className="cart-item-meta text-muted small">
      {parts.map((p, i) => <span key={i}>{p}&nbsp;</span>)}
    </div>
  ) : null;
};
